import axiosInstance from '../api';
import { notification } from 'antd';

export const getAllCoupons = async ({
  skip = 0,
  limit = 10,
  name = "",
  isActive = null,
  order = "desc",
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("skip", skip);
    params.append("limit", limit);
    if (name) params.append("name", name);
    if (order) params.append("order", order);
    if (isActive !== null && isActive !== undefined) params.append("is_active", isActive);

    const response = await axiosInstance.get(`/coupon/list?${params.toString()}`);
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Fetching Coupons",
      description: response?.data?.message || "Failed to fetch coupon list",
      duration: 3,
    });
  } catch (error) {
    notification.error({
      message: "Error Fetching Coupons",
      description: error.response?.data?.message || "Failed to fetch coupon list",
      duration: 3,
    });
    throw new Error("Error fetching coupon list");
  }
};

// List the coupons already assigned to a user (to show "already assigned" and
// stop re-assigning the same code in the Assign Coupon modal).
export const getUserCouponAssignments = async ({ user_id }) => {
  try {
    const response = await axiosInstance.get(`/coupon/user_assignments?user_id=${user_id}`);
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    return { assignments: [] };
  } catch (error) {
    return { assignments: [] };
  }
};

// Bulk assign one coupon to many users (all / specific). Records assignments
// only - emails are sent afterwards per successful row via sendCouponAssignmentEmail.
export const massAssignCoupon = async ({ coupon_id, recipient_type, user_ids = [] }) => {
  try {
    const response = await axiosInstance.post(`/coupon/mass_assign`, {
      coupon_id,
      recipient_type,
      user_ids,
    });
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Assigning Coupon",
      description: response?.data?.message || "Failed to assign coupon",
      duration: 3,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Assigning Coupon",
      description: error.response?.data?.message || "Failed to assign coupon",
      duration: 3,
    });
    return null;
  }
};

// Step 1 of 2: records the assignment only - no email sent yet.
export const assignCoupon = async ({ user_id, coupon_id }) => {
  try {
    const response = await axiosInstance.post(`/coupon/assign`, { user_id, coupon_id });
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Assigning Coupon",
      description: response?.data?.message || "Failed to assign coupon",
      duration: 3,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Assigning Coupon",
      description: error.response?.data?.message || "Failed to assign coupon",
      duration: 3,
    });
    throw new Error("Error assigning coupon");
  }
};

// Step 2 of 2: sends (or re-sends/retries) the coupon-code email for an
// existing assignment - separate call so a failed/suppressed send can be
// retried without creating a duplicate assignment record.
export const sendCouponAssignmentEmail = async ({ assignment_id }) => {
  try {
    const response = await axiosInstance.post(`/coupon/assign/send_email`, { assignment_id });
    if (response?.data?.status_code === 200) {
      const emailSent = response?.data?.data?.email_sent;
      if (emailSent) {
        notification.success({
          message: "Coupon Assigned",
          description: response?.data?.message || "Coupon emailed to the user.",
          duration: 3,
        });
      } else {
        notification.warning({
          message: "Coupon Assigned - Email Not Sent",
          description: response?.data?.message || "The email could not be sent.",
          duration: 4,
        });
      }
      return response?.data?.data;
    }
    notification.error({
      message: "Error Sending Coupon Email",
      description: response?.data?.message || "Failed to send coupon email",
      duration: 3,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Sending Coupon Email",
      description: error.response?.data?.message || "Failed to send coupon email",
      duration: 3,
    });
    throw new Error("Error sending coupon assignment email");
  }
};

export const downloadCouponAssignments = async ({
  search = "",
  couponId = null,
  platform = null,
  status = null,
  planId = null,
  startDate = null,
  endDate = null,
  order = "desc",
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (order) params.append("order", order);
    if (search) params.append("search", search);
    if (couponId) params.append("coupon_id", couponId);
    if (platform) params.append("platform", platform);
    if (status) params.append("status", status);
    if (planId) params.append("plan_id", planId);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await axiosInstance.get(`/coupon/export_assignment_list?${params.toString()}`, {
      responseType: "blob", // Ensures the response is treated as a file
    });

    if (response.status === 200) {
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // extract filename from Content-Disposition header
      const disposition = response.headers["content-disposition"];
      let filename = "Coupon_assignment.xlsx"; // fallback
      if (disposition && disposition.includes("filename=")) {
        filename = disposition
          .split("filename=")[1]
          .replace(/['"]/g, ""); // remove quotes if present
      }
      a.download = filename; // Set the filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return response;
    } else {
      throw new Error("Download failed Coupon assignments");
    }
  } catch (error) {
    console.error("Error downloading Coupon assignments:", error);
    notification.error({
      message: "Error Downloading Coupon Assignments",
      description: error.response?.data?.message || "Failed to download coupon assignments.",
      duration: 3,
    });
    throw error;
  }
};

// The main Coupon Assignment table. `status` and `platform` are derived
// server-side (platform is resolved live from user_session, not the mostly-NULL
// stored column), so they are plain query params like the rest.
export const getCouponAssignments = async ({
  skip = 0,
  limit = 10,
  search = "",
  couponId = null,
  platform = null,
  status = null,
  planId = null,
  startDate = null,
  endDate = null,
  order = "desc",
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("skip", skip);
    params.append("limit", limit);
    params.append("order", order);
    if (search) params.append("search", search);
    if (couponId) params.append("coupon_id", couponId);
    if (platform) params.append("platform", platform);
    if (status) params.append("status", status);
    if (planId) params.append("plan_id", planId);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await axiosInstance.get(`/coupon/assignment_list?${params.toString()}`);
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Fetching Assignments",
      description: response?.data?.message || "Failed to fetch coupon assignments",
      duration: 3,
    });
    return { total_count: 0, assignments: [] };
  } catch (error) {
    notification.error({
      message: "Error Fetching Assignments",
      description: error.response?.data?.message || "Failed to fetch coupon assignments",
      duration: 3,
    });
    return { total_count: 0, assignments: [] };
  }
};

// Dry run behind "Add to list" - writes nothing. Tells us whether the coupon
// draws from a store-code pool and whether that pool has enough codes left for
// the selected users, so we can stop before assigning and handing someone a
// code that was never meant for them.
export const previewCouponAssignment = async ({ coupon_id, user_ids = [] }) => {
  try {
    const response = await axiosInstance.post(`/coupon/assign/preview`, { coupon_id, user_ids });
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Previewing Assignment",
      description: response?.data?.message || "Failed to preview coupon assignment",
      duration: 3,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Previewing Assignment",
      description: error.response?.data?.message || "Failed to preview coupon assignment",
      duration: 3,
    });
    return null;
  }
};

// Removing an assignment releases its reserved store code back to the pool
// (backend side) - redeemed assignments are refused with a 400.
export const deleteCouponAssignment = async (assignment_id) => {
  try {
    const response = await axiosInstance.delete(`/coupon/assignment/${assignment_id}`);
    if (response?.data?.status_code === 200) {
      notification.success({
        message: "Assignment Removed",
        description: response?.data?.message || "Assignment removed.",
        duration: 3,
      });
      return response?.data?.data;
    }
    notification.error({
      message: "Error Removing Assignment",
      description: response?.data?.message || "Failed to remove assignment",
      duration: 4,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Removing Assignment",
      description: error.response?.data?.message || "Failed to remove assignment",
      duration: 4,
    });
    return null;
  }
};

// ---------------------------------------------------------------------------
// iOS offer codes
//
// An iOS coupon is backed by a finite pool of one-time Apple codes: assigning
// to an iOS user takes one out of that pool. These calls load a batch and show
// what is left, so the pool is topped up BEFORE assignment starts failing with
// "No iOS offer codes left".
// ---------------------------------------------------------------------------

// Per (coupon, plan) pool counts. `is_low` is the backend's own threshold - we
// surface it rather than re-deciding "low" here, so the warning matches the
// point at which assignment actually starts to be at risk.
export const getOfferCodePool = async () => {
  try {
    const response = await axiosInstance.get(`/coupon/offer_codes/pool`);
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Fetching Offer Code Pool",
      description: response?.data?.message || "Failed to fetch offer code pool",
      duration: 3,
    });
    return { pools: [] };
  } catch (error) {
    notification.error({
      message: "Error Fetching Offer Code Pool",
      description: error.response?.data?.message || "Failed to fetch offer code pool",
      duration: 3,
    });
    return { pools: [] };
  }
};

// multipart/form-data, not JSON - the payload is Apple's exported CSV.
// Axios fills in the multipart boundary itself for a FormData body.
export const uploadOfferCodes = async ({
  coupon_id,
  plan_id,
  ios_offer_reference_name,
  expires_at = null,
  file,
}) => {
  try {
    const formData = new FormData();
    formData.append("coupon_id", coupon_id);
    formData.append("plan_id", plan_id);
    formData.append("ios_offer_reference_name", ios_offer_reference_name);
    if (expires_at) formData.append("expires_at", expires_at);
    formData.append("file", file);

    const response = await axiosInstance.post(`/coupon/offer_codes/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Uploading Offer Codes",
      description: response?.data?.message || "Failed to upload offer codes",
      duration: 4,
    });
    return null;
  } catch (error) {
    // 404 = coupon/plan pair has no offer row, 409 = reference name taken by
    // another coupon, 400 = unusable file. The API's own message says which,
    // so it is shown rather than replaced with a generic one.
    notification.error({
      message: "Error Uploading Offer Codes",
      description: error.response?.data?.message || "Failed to upload offer codes",
      duration: 5,
    });
    return null;
  }
};

// Individual codes - the audit view behind the pool counts. `user_id` answers
// the support question "which code did THIS user get, and did it work".
export const getOfferCodes = async ({
  couponId = null,
  planId = null,
  status = null,
  userId = null,
  skip = 0,
  limit = 50,
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("skip", skip);
    params.append("limit", limit);
    if (couponId) params.append("coupon_id", couponId);
    if (planId) params.append("plan_id", planId);
    if (status) params.append("status", status);
    if (userId) params.append("user_id", userId);

    const response = await axiosInstance.get(`/coupon/offer_codes?${params.toString()}`);
    if (response?.data?.status_code === 200) {
      return response?.data?.data;
    }
    notification.error({
      message: "Error Fetching Offer Codes",
      description: response?.data?.message || "Failed to fetch offer codes",
      duration: 3,
    });
    return { total_count: 0, codes: [] };
  } catch (error) {
    notification.error({
      message: "Error Fetching Offer Codes",
      description: error.response?.data?.message || "Failed to fetch offer codes",
      duration: 3,
    });
    return { total_count: 0, codes: [] };
  }
};

// Returns an assigned code to the pool - the code is still unused at Apple, so
// it is worth recovering rather than burning. Redeemed codes are refused (400).
export const releaseOfferCode = async ({ offer_code_id }) => {
  try {
    const response = await axiosInstance.post(`/coupon/offer_codes/release`, { offer_code_id });
    if (response?.data?.status_code === 200) {
      notification.success({
        message: "Code Released",
        description: response?.data?.message || "Offer code returned to the pool.",
        duration: 3,
      });
      return response?.data?.data;
    }
    notification.error({
      message: "Error Releasing Code",
      description: response?.data?.message || "Failed to release offer code",
      duration: 4,
    });
    return null;
  } catch (error) {
    notification.error({
      message: "Error Releasing Code",
      description: error.response?.data?.message || "Failed to release offer code",
      duration: 4,
    });
    return null;
  }
};

// Whether the logged-in admin may see the Plan Configuration button and the
// Offer Codes tab. Decided server-side from the caller's token email against
// the plan_and_offer_config_allowed_emails config value. Any failure returns
// false so the gated UI stays hidden rather than flashing open on an error.
export const getPlanAndOfferConfigAccess = async () => {
  try {
    const response = await axiosInstance.get(`/coupon/plan_and_offer_config_access`);
    if (response?.data?.status_code === 200) {
      return !!response?.data?.data?.is_allowed;
    }
    return false;
  } catch (error) {
    return false;
  }
};
